import { useCallback } from 'react'
import type { TaskDescriptionEditorProps } from '../type'
import { TiptapRichTextEditor } from '@/components/editor/TiptapRichTextEditor'
import { useEditorAutoSave } from '@/components/editor/useEditorAutoSave'
import { DEFAULT_FORMAT_TOOLBAR_ITEMS } from '@/components/editor/toolbarDefaults'
import {
  useEntityLinkMutations,
  useEntityLinks,
  useTaskContent,
  useTaskContentMutations,
} from '@/lib/db/rxdb/hooks'
import { ENTITY_TYPES } from '@/lib/constants/entityTypes'
import useLoginStore from '@/store/LoginStore'

const EDITOR_NAMESPACE = 'TaskDescriptionEditor'

/**
 * Task description editor using separate content sync.
 * Uses useEditorAutoSave for debounced persistence and entity link reconciliation.
 */
export function TaskDescriptionEditor({
  task,
  canEdit,
  onPasteFiles,
}: TaskDescriptionEditorProps) {
  const { content } = useTaskContent(task?.id)
  const { createTaskContent, updateTaskContent } = useTaskContentMutations()
  const { links: existingLinks } = useEntityLinks(task?.id)
  const { createEntityLink, deleteEntityLink } = useEntityLinkMutations()
  const userId = useLoginStore((state) => state.userId)

  const createContent = useCallback(
    async (data: { content: string; format: string; length: number; plainText: string; version: number }) => {
      if (!task) return
      await createTaskContent({
        taskId: task.id,
        ...data,
      })
    },
    [task, createTaskContent],
  )

  const updateContent = useCallback(
    async (id: string, data: { content: string; format: string; length: number; plainText: string; version: number }) => {
      await updateTaskContent(id, data)
    },
    [updateTaskContent],
  )

  const { displayContent, handleContentChange } = useEditorAutoSave({
    entityId: task?.id,
    entityType: ENTITY_TYPES.WORK_ITEM,
    entityName: task?.name || '',
    envId: task?.envId,
    userId,
    content,
    existingLinks,
    canEdit,
    createContent,
    updateContent,
    createEntityLink,
    deleteEntityLink,
  })

  if (!task) {
    return (
      <div className="rounded-md border border-dashed border-border bg-muted p-4 text-sm text-muted-foreground">
        Task description is not available yet.
      </div>
    )
  }

  return (
    <TiptapRichTextEditor
      key={`${task.id}-${content?.id || 'no-content'}`}
      namespace={`${EDITOR_NAMESPACE}-${task.id}`}
      initialContent={displayContent}
      format={content?.format as 'tiptap' | 'markdown' | 'plain' | undefined}
      editable={canEdit}
      placeholder="Add more details about this task..."
      contentEditableClassName="py-0"
      placeholderClassName="top-0"
      onContentChange={handleContentChange}
      onPasteFiles={canEdit ? onPasteFiles : undefined}
      ariaLabel="Task description editor"
      enableEntityLinks
      enableMentions
      enableTables
      toolbarItems={DEFAULT_FORMAT_TOOLBAR_ITEMS}
    />
  )
}
